import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Tsabt } from '@data';
import { TranslocoDirective } from '@jsverse/transloco';
import { MultiStateIndicatorComponent, StateIndicator, TooltipDirective } from '@ui';
import { IndicatorState } from '../../../models/indicator.models';
import { IndicatorStates } from '../../../constants/indicators.constants';

@Component({
  selector: 'ui-outputs-bt-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MultiStateIndicatorComponent, TooltipDirective, TranslocoDirective],
  template: `
    <ng-container *transloco="let t">
      @if (states && indicators()) {
        <ui-multi-state-indicator [indicators]="indicators()" [states]="states" />
      }
      <ng-template #voidContent let-data>
        <div
          uiTooltip
          [tooltipTitle]="isDisabled() ? null : tooltipTitleTemplate"
          [tooltipTitleContext]="{ $implicit: data }"
          [tooltipTrigger]="this.isDisabled() ? null : 'hover'"
          class="w-3 h-3 rounded-full transition-all duration-300 border bg-white border-neutral-6"
        ></div>
      </ng-template>

      <ng-template #freeContent let-data>
        <div
          uiTooltip
          [tooltipTitle]="isDisabled() ? null : tooltipTitleTemplate"
          [tooltipTitleContext]="{ $implicit: data }"
          class="w-3 h-3 rounded-full transition-all duration-300 border bg-white border-neutral-6"
        >
          <div class="flex justify-center items-center h-full w-full">
            <div class="border border-neutral-6 rounded-full w-1.5"></div>
          </div>
        </div>
      </ng-template>

      <ng-template #isDisabledContent let-data>
        <div
          uiTooltip
          [tooltipTitle]="isDisabled() ? null : tooltipTitleTemplate"
          [tooltipTitleContext]="{ $implicit: data }"
          class="w-3 h-3 rounded-full transition-all duration-300"
          [class]="isDisabled() ? 'bg-neutral-6' : 'bg-primary'"
        >
          <div class="flex justify-center items-center h-full w-full">
            <div class="border border-white rounded-full w-1.5"></div>
          </div>
        </div>
      </ng-template>

      <ng-template #occupiedWithoutTsabtContent let-data>
        <div
          uiTooltip
          [tooltipTitle]="isDisabled() ? null : tooltipTitleTemplate"
          [tooltipTitleContext]="{ $implicit: data }"
          class="w-3 h-3 rounded-full transition-all duration-300"
          [class]="isDisabled() ? 'bg-neutral-6' : 'bg-primary'"
        ></div>
      </ng-template>

      <ng-template #tooltipTitleTemplate let-data>
        <div class="flex flex-col text-xs">
          <span
            >{{ t('DISTRIBUTION_NETWORK.TABLE.OUTPUT') }}:
            {{ data?.outputId || t('DISTRIBUTION_NETWORK.TABLE.WITHOUT_OUTPUT') }}
          </span>
          <span
            >{{ t('DISTRIBUTION_NETWORK.TABLE.CARD_ID') }}:
            {{ data?.id || t('DISTRIBUTION_NETWORK.TABLE.WITHOUT_CARD') }}
          </span>
        </div>
      </ng-template>
    </ng-container>
  `,
})
export class OutputsBtIndicatorComponent implements AfterViewInit {
  private cdr = inject(ChangeDetectorRef);

  state = input<IndicatorState>(IndicatorStates.Default);
  indicators = input<Tsabt[]>([]);

  isDisabled = computed<boolean>(() => this.state() === IndicatorStates.Disabled);

  @ViewChild('voidContent') voidContent!: TemplateRef<any>;
  @ViewChild('freeContent') freeContent!: TemplateRef<any>;
  @ViewChild('isDisabledContent') isDisabledContent!: TemplateRef<any>;
  @ViewChild('occupiedWithoutTsabtContent') occupiedWithoutTsabtContent!: TemplateRef<any>;

  states: StateIndicator | null = null;

  ngAfterViewInit(): void {
    this.states = {
      empty: { content: this.voidContent },
      free: { content: this.freeContent },
      occupied: { content: this.isDisabledContent },
      occupiedWithoutTsabt: { content: this.occupiedWithoutTsabtContent },
    };
    this.cdr.detectChanges();
  }
}
